import { Button, Dropdown, TooltipV2 } from '@cimplico/components';
import { ChatBubbleLeftRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Reply } from '../../../types/reply';
import { UserOverview } from '../../../types/user';
import { calcRelativeTime, formatDateTime } from '../../../utilities/formatting';
import Editor from '../../rich-text-editor/Editor';
import UserAvatar from '../../avatar/UserAvatar';
import NewReply from './NewReply';
import ReplyRow from './ReplyRow';
import EmptyStateNew from '../../empty-state/EmptyStateNew';
import { InternalCommentReply } from '../../../types/internal-comment';
import { ClientQueryReply } from '../../../types/client-query';

type ThreadDetailsProps = {
    statusBadge: ReactNode;
    onClose: () => void;
    createdBy: UserOverview;
    structuredContent?: string;
    context?: { label: string; url: string };
    createdAt: Date;
    canReply: boolean;
    replies: (InternalCommentReply | ClientQueryReply)[];
    actions?: { label: string; value: string; hidden: boolean }[];
    onActionsClick?: (value: string) => void;
    onReply?: (rawReply: string, structuredReply: string, setResolve: boolean) => void;
    onReplyEdit?: (replyId: string, rawReply: string, structuredReply: string, setResolve: boolean) => void;
    onReplyDelete?: (replyId: string) => void;
    disableAllActions?: boolean;
};

function ThreadDetails({
    statusBadge,
    onClose,
    createdAt,
    structuredContent,
    createdBy,
    canReply,
    replies,
    context,
    actions = [],
    onActionsClick,
    onReply,
    onReplyEdit,
    onReplyDelete,
    disableAllActions = false,
}: ThreadDetailsProps): JSX.Element {
    const [creatingReply, setCreatingReply] = useState(false);
    const [editingReplyId, setEditingReplyId] = useState<string | undefined>();
    const [editingReplyMessage, setEditingReplyMessage] = useState<{ rawReply?: string; structuredReply?: string }>({});

    const messagesEndRef = useRef<null | HTMLDivElement>(null);

    function scrollToBottom(): void {
        messagesEndRef && messagesEndRef.current?.scrollIntoView();
    }

    useEffect(() => {
        scrollToBottom();
    }, [replies]);

    function handleReply(rawReply: string, structuredReply: string, setResolve: boolean): void {
        if (editingReplyId && editingReplyMessage) {
            onReplyEdit && onReplyEdit(editingReplyId, rawReply, structuredReply, setResolve);
        } else {
            onReply && onReply(rawReply, structuredReply, setResolve);
        }
        resetSelection();
    }

    function resetSelection(): void {
        setCreatingReply(false);
        setEditingReplyId(undefined);
        setEditingReplyMessage({});
    }

    return (
        <div className="h-[725px] pl-6 pr-2 pt-2">
            <div className="flex gap-2 items-center ">
                {/* CONTEXT */}
                {context ? (
                    <Link to={context.url} className="text-xs text-indigo-600 hover:underline">
                        {context.label}
                    </Link>
                ) : (
                    <p className="text-xs text-gray-600">-</p>
                )}
                {statusBadge}

                <div className="ml-auto flex items-center">
                    <Dropdown dotsDirection="horizontal" disabled={disableAllActions}>
                        {actions
                            .filter((a) => !a.hidden)
                            .map((action) => {
                                return (
                                    <Dropdown.Option
                                        label={action.label}
                                        value={action.value}
                                        key={action.value}
                                        onClick={(_, e): void => {
                                            e?.stopPropagation();
                                            onActionsClick && onActionsClick(action.value);
                                        }}
                                    />
                                );
                            })}
                    </Dropdown>
                    <Button type="hollow" size="sm" leftIcon={<XMarkIcon />} onClick={onClose} />
                </div>
            </div>
            <div>
                <Editor defaultContent={structuredContent} readOnly />
            </div>

            {/* AUTHOR */}
            <div className="mt-4 flex items-start border-b pb-4">
                <UserAvatar user={createdBy} />

                <div className="ml-2">
                    <p className="text-xs font-medium text-gray-800">
                        {createdBy.firstName} {createdBy.lastName}
                    </p>
                    <TooltipV2 message={formatDateTime(createdAt)}>
                        <p className="text-gray-500 text-xs">{calcRelativeTime(createdAt)}</p>
                    </TooltipV2>
                </div>
            </div>

            {/* REPLIES */}
            <div className="flex justify-between border-b pb-1 items-center">
                <p className="text-sm font-medium text-gray-800">{replies.length === 1 ? `1 reply` : `${replies.length} replies`}</p>
            </div>
            <div className="h-[550px] flex flex-col justify-between">
                <div className={`overflow-auto h-full`}>
                    {replies.length === 0 && (
                        <div className="mt-8">
                            <EmptyStateNew
                                icon={<ChatBubbleLeftRightIcon />}
                                helpLink="https://learn.cimplico.com/docs/internal-comments-overview#reading-replying-to-and-resolving-comments"
                                helpText="Add a reply in this thread."
                                title="No replies"
                                disableActionButton={disableAllActions}
                            />
                        </div>
                    )}
                    {replies.map((reply) => {
                        return (
                            <ReplyRow
                                key={reply.id}
                                reply={reply}
                                disableActions={disableAllActions || creatingReply || editingReplyMessage.rawReply !== undefined}
                                showMenu={canReply}
                                onDelete={(): void => onReplyDelete && onReplyDelete(reply.id)}
                                onEdit={(rawReply, structuredReply): void => {
                                    setEditingReplyId(reply.id);
                                    setEditingReplyMessage({ rawReply, structuredReply });
                                }}
                            />
                        );
                    })}
                    <div ref={messagesEndRef} />
                </div>
                <div>
                    {canReply && (
                        <NewReply
                            defaultRawReply={editingReplyMessage.rawReply}
                            defaultStructuredReply={editingReplyMessage.structuredReply}
                            onReply={handleReply}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
export default ThreadDetails;
